import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { WasteTrackingForm } from '../components/forms/WasteTrackingForm';
import { Plus, Trash2, Building2, Calendar, TrendingUp, TrendingDown, BarChart3, Package, AlertTriangle, CheckCircle, CreditCard as Edit, Eye, Download, FileText, X } from 'lucide-react';

interface WasteQuantity {
  id: string;
  site_id: string;
  waste_type: string;
  amount: number;
  unit: string;
  created_at: string;
  updated_at: string;
}

interface WasteTracking {
  id: string;
  waste_quantity_id: string;
  amount: number;
  date: string;
  notes?: string;
  receipt_photo_url?: string;
  created_at: string;
  updated_at: string;
}

interface Site {
  id: string;
  name: string;
  address: string;
  type: string;
  status: string;
}

interface WasteTrackingWithDetails extends WasteTracking {
  waste_quantity?: WasteQuantity & {
    site?: Site;
  };
}

interface WasteSummary {
  waste_type: string;
  estimated_amount: number;
  tracked_amount: number;
  unit: string;
  percentage_complete: number;
  site_name: string;
  site_id: string;
  waste_quantity_id: string;
}

export const WasteTrackingPage: React.FC = () => {
  const [wasteTrackings, setWasteTrackings] = useState<WasteTrackingWithDetails[]>([]);
  const [wasteSummaries, setWasteSummaries] = useState<WasteSummary[]>([]);
  const [siteCompletionData, setSiteCompletionData] = useState<{[key: string]: {totalEstimated: number, totalTracked: number, percentage: number}}>({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTracking, setEditingTracking] = useState<WasteTrackingWithDetails | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedSite, setSelectedSite] = useState<string>(''); // Keep empty by default
  const [sites, setSites] = useState<Site[]>([]);
  const [viewingReceipt, setViewingReceipt] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      await Promise.all([
        fetchWasteTrackings(),
        fetchWasteSummaries(),
        fetchSites(),
        calculateSiteCompletionData()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSites = async () => {
    try {
      const { data, error } = await supabase
        .from('sites')
        .select('id, name, address, type, status')
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      setSites(data || []);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const fetchWasteTrackings = async () => {
    try {
      const { data, error } = await supabase
        .from('waste_tracking')
        .select(`
          *,
          waste_quantities!inner(
            *,
            sites!inner(*)
          )
        `)
        .order('date', { ascending: false });

      if (error) throw error;

      const trackingsWithDetails: WasteTrackingWithDetails[] = (data || []).map(tracking => ({
        ...tracking,
        waste_quantity: {
          ...tracking.waste_quantities,
          site: tracking.waste_quantities.sites
        }
      }));

      setWasteTrackings(trackingsWithDetails);
    } catch (error) {
      console.error('Error fetching waste trackings:', error);
    }
  };

  const calculateSiteCompletionData = async () => {
    try {
      // Get all waste quantities with their tracking data
      const { data: wasteQuantitiesData, error } = await supabase
        .from('waste_quantities')
        .select(`
          *,
          sites!inner(id, name, address, status)
        `)
        .eq('sites.status', 'active');

      if (error) throw error;

      const siteData: {[key: string]: {totalEstimated: number, totalTracked: number, percentage: number}} = {};

      // Group by site and calculate totals
      for (const quantity of wasteQuantitiesData || []) {
        const siteId = quantity.site_id;
        
        if (!siteData[siteId]) {
          siteData[siteId] = { totalEstimated: 0, totalTracked: 0, percentage: 0 };
        }

        // Add to estimated total
        siteData[siteId].totalEstimated += Number(quantity.amount);

        // Get tracked amount for this waste quantity
        const { data: trackingData } = await supabase
          .from('waste_tracking')
          .select('amount')
          .eq('waste_quantity_id', quantity.id);

        const trackedAmount = (trackingData || []).reduce((sum, track) => sum + Number(track.amount), 0);
        siteData[siteId].totalTracked += trackedAmount;
      }

      // Calculate percentages
      Object.keys(siteData).forEach(siteId => {
        const data = siteData[siteId];
        data.percentage = data.totalEstimated > 0 ? (data.totalTracked / data.totalEstimated) * 100 : 0;
      });

      setSiteCompletionData(siteData);
    } catch (error) {
      console.error('Error calculating site completion data:', error);
    }
  };

  const fetchWasteSummaries = async () => {
    try {
      const { data, error } = await supabase
        .from('waste_quantities')
        .select(`
          *,
          sites!inner(id, name, address, type, status)
        `)
        .eq('sites.status', 'active')
        .order('sites(name)');

      if (error) throw error;

      // Calculate summaries with tracked amounts
      const summaries: WasteSummary[] = [];
      
      for (const wasteQuantity of data || []) {
        // Get total tracked amount for this waste quantity
        const { data: trackingData, error: trackingError } = await supabase
          .from('waste_tracking')
          .select('amount')
          .eq('waste_quantity_id', wasteQuantity.id);

        if (trackingError) {
          console.error('Error fetching tracking data:', trackingError);
          continue;
        }

        const trackedAmount = (trackingData || []).reduce((sum, track) => sum + Number(track.amount), 0);
        const estimatedAmount = Number(wasteQuantity.amount);
        const percentageComplete = estimatedAmount > 0 ? (trackedAmount / estimatedAmount) * 100 : 0;

        summaries.push({
          waste_type: wasteQuantity.waste_type,
          estimated_amount: estimatedAmount,
          tracked_amount: trackedAmount,
          unit: wasteQuantity.unit || 'tonnes', // Fallback to tonnes if unit is null
          percentage_complete: Math.min(percentageComplete, 100),
          site_name: wasteQuantity.sites.name,
          site_id: wasteQuantity.sites.id,
          waste_quantity_id: wasteQuantity.id
        });
      }

      setWasteSummaries(summaries);
    } catch (error) {
      console.error('Error fetching waste summaries:', error);
    }
  };

  const handleSubmit = async (trackingData: Partial<WasteTracking>) => {
    setSubmitting(true);
    try {
      if (editingTracking) {
        const { error } = await supabase
          .from('waste_tracking')
          .update(trackingData)
          .eq('id', editingTracking.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('waste_tracking')
          .insert([trackingData]);
        
        if (error) throw error;
      }
      
      setShowModal(false);
      setEditingTracking(null);
      fetchData();
    } catch (error) {
      console.error('Error saving waste tracking:', error);
      alert('Failed to save waste tracking entry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (trackingId: string) => {
    if (confirm('Are you sure you want to delete this waste tracking entry? This action cannot be undone.')) {
      try {
        const { error } = await supabase
          .from('waste_tracking')
          .delete()
          .eq('id', trackingId);

        if (error) throw error;
        fetchData();
      } catch (error) {
        console.error('Error deleting waste tracking:', error);
        alert('Failed to delete waste tracking entry. Please try again.');
      }
    }
  };

  const handleViewReceipt = (url: string) => {
    setViewingReceipt(url);
  };

  const handleDownloadReceipt = async (url: string, trackingId: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      // Extract filename from URL or create a default one
      const filename = url.split('/').pop() || `waste-receipt-${trackingId}.jpg`;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading receipt:', error);
      alert('Failed to download receipt. Please try again.');
    }
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 100) return 'bg-red-500';
    if (percentage >= 80) return 'bg-yellow-500';
    if (percentage >= 60) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getStatusIcon = (percentage: number) => {
    if (percentage >= 100) return <AlertTriangle className="h-4 w-4 text-red-600" />;
    if (percentage >= 80) return <TrendingUp className="h-4 w-4 text-yellow-600" />;
    return <CheckCircle className="h-4 w-4 text-green-600" />;
  };

  const filteredTrackings = wasteTrackings.filter(tracking => {
    const siteMatch = !selectedSite || tracking.waste_quantity?.site?.id === selectedSite;
    return siteMatch;
  });

  const filteredSummaries = wasteSummaries.filter(summary => {
    const siteMatch = !selectedSite || summary.site_id === selectedSite;
    return siteMatch;
  });

  const uniqueWasteTypes = [...new Set(wasteSummaries.map(s => s.waste_type))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Waste Tracking</h1>
          <p className="text-sm text-gray-600 mt-1">
            Track daily waste removal progress against site estimates
          </p>
        </div>
        <button
          onClick={() => {
            setEditingTracking(null);
            setShowModal(true);
          }}
          className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Add Waste Entry</span>
        </button>
      </div>

      {/* Site Selection Cards */}
      {!selectedSite && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Select a Site</h3>
            <p className="text-sm text-gray-500 mt-1">Choose a site to view and manage waste tracking</p>
          </div>
          
          <div className="p-6">
            {sites.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sites.map((site) => {
                  const completionData = siteCompletionData[site.id] || { totalEstimated: 0, totalTracked: 0, percentage: 0 };
                  return (
                    <div
                      key={site.id}
                      onClick={() => setSelectedSite(site.id)}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-orange-300 transition-all cursor-pointer group"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-orange-600" />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-lg font-medium text-gray-900 group-hover:text-orange-600 transition-colors">
                              {site.name}
                            </h4>
                            <p className="text-sm text-gray-500">{site.address}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="mt-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-700">Waste Removal Progress</span>
                          <span className="text-sm font-semibold text-gray-900">
                            {completionData.percentage.toFixed(1)}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className={`h-3 rounded-full transition-all duration-300 ${getProgressColor(completionData.percentage)}`}
                            style={{ width: `${Math.min(completionData.percentage, 100)}%` }}
                          ></div>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>Tracked: {completionData.totalTracked.toFixed(1)} tonnes</span>
                          <span>Est: {completionData.totalEstimated.toFixed(1)} tonnes</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No active sites found</h3>
                <p className="text-gray-500 mb-4">Create sites first to track waste removal.</p>
                <Link
                  to="/sites"
                  className="text-orange-600 hover:text-orange-700 text-sm font-medium"
                >
                  Manage Sites
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Back to Site Selection */}
      {selectedSite && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="h-8 w-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <Building2 className="h-4 w-4 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {sites.find(s => s.id === selectedSite)?.name}
                </h3>
                <p className="text-sm text-gray-500">
                  {sites.find(s => s.id === selectedSite)?.address}
                </p>
              </div>
            </div>
            <button
              onClick={() => setSelectedSite('')}
              className="text-orange-600 hover:text-orange-700 text-sm font-medium"
            >
              ← Back to Site Selection
            </button>
          </div>
        </div>
      )}

      {/* Waste Progress Summary - Only show when site is selected */}
      {selectedSite && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="h-8 w-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="h-4 w-4 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">Waste Removal Progress</h3>
          </div>
        </div>
        
        <div className="p-6">
          {filteredSummaries.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredSummaries.map((summary, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Package className="h-4 w-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-900 capitalize">
                        {summary.waste_type.replace('_', ' ')}
                      </span>
                    </div>
                    {getStatusIcon(summary.percentage_complete)}
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs text-gray-600">
                      <span>Progress</span>
                      <span>{summary.percentage_complete.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(summary.percentage_complete)}`}
                        style={{ width: `${Math.min(summary.percentage_complete, 100)}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-600">
                      <span>Tracked: {summary.tracked_amount} {summary.unit === 'cubic_metres' ? 'm³' : summary.unit}</span>
                      <span>Est: {summary.estimated_amount} {summary.unit === 'cubic_metres' ? 'm³' : summary.unit}</span>
                    </div>
                    
                    {/* Warning for over-tracking */}
                    {(summary.estimated_amount === 0 && summary.tracked_amount > 0) || summary.percentage_complete > 100 ? (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                        <div className="flex items-center space-x-1">
                          <AlertTriangle className="h-3 w-3 text-red-600" />
                          <span className="text-xs font-medium text-red-800">
                            {summary.estimated_amount === 0 
                              ? 'No estimate set - tracking without baseline'
                              : `Over estimate by ${(summary.tracked_amount - summary.estimated_amount).toFixed(2)} ${summary.unit === 'cubic_metres' ? 'm³' : summary.unit}`
                            }
                          </span>
                        </div>
                      </div>
                    ) : null}
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">{summary.site_name}</span>
                      <span className={`text-xs font-medium ${
                        (summary.estimated_amount === 0 && summary.tracked_amount > 0) || summary.percentage_complete > 100
                          ? 'text-red-600'
                          : 'text-gray-600'
                      }`}>
                        Tracked: {summary.tracked_amount} {summary.unit === 'cubic_metres' ? 'm³' : summary.unit}
                      </span>
                      <Link
                        to={`/sites/${summary.site_id}`}
                        className="text-xs text-orange-600 hover:text-orange-700"
                      >
                        View Site
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No waste estimates found</h3>
              <p className="text-gray-500 mb-4">Set up waste estimates for sites to track progress.</p>
              <Link
                to="/sites"
                className="text-orange-600 hover:text-orange-700 text-sm font-medium"
              >
                Manage Sites
              </Link>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Waste Tracking Entries - Only show when site is selected */}
      {selectedSite && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center">
                <Calendar className="h-4 w-4 text-green-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">Daily Tracking Entries</h3>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-500">
                {filteredTrackings.length} entries
              </span>
              <button
                onClick={() => {
                  setEditingTracking(null);
                  setShowModal(true);
                }}
                className="bg-orange-600 text-white px-3 py-1.5 rounded-md flex items-center space-x-1 hover:bg-orange-700 transition-colors text-sm"
              >
                <Plus className="h-4 w-4" />
                <span>Add Waste Entry</span>
              </button>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Site
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Waste Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Receipt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTrackings.map((tracking) => (
                <tr key={tracking.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-900">
                        {new Date(tracking.date).toLocaleDateString()}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {tracking.waste_quantity?.site?.name}
                      </div>
                      <div className="text-sm text-gray-500 capitalize">
                        {tracking.waste_quantity?.site?.type?.replace('_', ' ')}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <Package className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-900 capitalize">
                        {tracking.waste_quantity?.waste_type?.replace('_', ' ')}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 font-medium">
                      {tracking.amount} {tracking.waste_quantity?.unit === 'cubic_metres' ? 'm³' : tracking.waste_quantity?.unit}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate">
                      {tracking.notes || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {tracking.receipt_photo_url ? (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewReceipt(tracking.receipt_photo_url!)}
                          className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
                          title="View Receipt"
                        >
                          <Eye className="h-4 w-4" />
                          <span className="text-xs">View</span>
                        </button>
                        <button
                          onClick={() => handleDownloadReceipt(tracking.receipt_photo_url!, tracking.id)}
                          className="text-green-600 hover:text-green-900 flex items-center space-x-1"
                          title="Download Receipt"
                        >
                          <Download className="h-4 w-4" />
                          <span className="text-xs">Download</span>
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-1 text-gray-400">
                        <FileText className="h-4 w-4" />
                        <span className="text-xs">No receipt</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setEditingTracking(tracking);
                          setShowModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit Entry"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(tracking.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Entry"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredTrackings.length === 0 && (
          <div className="text-center py-12">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tracking entries found</h3>
            <p className="text-gray-500 mb-4">
              {selectedSite 
                ? 'Try adjusting your filter or add new tracking entries.'
                : 'Start tracking daily waste removal by adding your first entry.'
              }
            </p>
            <button
              onClick={() => {
                setEditingTracking(null);
                setShowModal(true);
              }}
              className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
            >
              Add First Entry
            </button>
          </div>
        )}
      </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {editingTracking ? 'Edit Waste Entry' : 'Add Waste Entry'}
              </h3>
            </div>
            <div className="p-6">
              <WasteTrackingForm
                initialData={editingTracking}
                onSubmit={handleSubmit}
                onCancel={() => {
                  setShowModal(false);
                  setEditingTracking(null);
                }}
                submitting={submitting}
              />
            </div>
          </div>
        </div>
      )}

      {viewingReceipt && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50"
          onClick={() => setViewingReceipt(null)}
        >
          <div
            className="relative bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-medium text-gray-900">Receipt Photo</h3>
              <button
                onClick={() => setViewingReceipt(null)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <img
                src={viewingReceipt}
                alt="Receipt"
                className="max-w-full h-auto rounded-lg"
              />
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={() => handleDownloadReceipt(viewingReceipt, 'receipt')}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>Download</span>
              </button>
              <button
                onClick={() => setViewingReceipt(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700">Filter by Site:</label>
          <select
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value="">All Sites</option>
            {sites.map(site => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={() => {
            setEditingTracking(null);
            setShowModal(true);
          }}
          className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Add Waste Entry</span>
        </button>
      </div>
    </div>
  );
};